import { cn } from '@/lib/utils'
import type {
  ActivitySourceCode, ActivityTypeCode, AdoptionStatusCode, BadgeCode,
  CoverageStatusCode, MissionKindCode, MissionStatusCode, MobilizerRoleCode,
  MobilizerStatusCode, TaskPriorityCode, TaskStatusCode, TierCode,
} from '@/shared/api/types'

/**
 * ONE vocabulary module for the whole feature.
 *
 * PollSphere defined `ROLE_META` three times and `ACTIVITY_TYPE_META` twice, and
 * the copies had already drifted — "Survey Collection" in one file, "Survey
 * Capture" in another, `rose` for HIGH priority in one and `red` in the next.
 * Every label and every colour in this feature comes from here.
 *
 * ## Colour never carries meaning alone
 *
 * Every chip below renders its label. The colour is reinforcement, so the
 * screens stay readable in greyscale, in print, and to the ~8% of men with a
 * red-green deficiency. An unrecognised code falls through to neutral rather
 * than throwing: a newer backend must render, not crash.
 *
 * ## Both themes, always
 *
 * PollSphere had no `dark:` variant anywhere in this module — every badge was a
 * light-mode pastel that turns into a glowing block on a dark surface. Each tone
 * here is declared for both.
 */

const CHIP = 'inline-flex items-center gap-1.5 px-2 py-0.5 text-erp-xs font-medium border rounded-md whitespace-nowrap'

type Tone = 'neutral' | 'positive' | 'caution' | 'serious' | 'critical' | 'info' | 'accent' | 'special'

const TONE: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground border-border',
  positive: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900',
  caution: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
  serious: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-900',
  critical: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900',
  info: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900',
  accent: 'bg-chocolate-soft/60 text-chocolate border-chocolate-muted',
  special: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-900',
}

const TONE_DOT: Record<Tone, string> = {
  neutral: 'bg-slate-400',
  positive: 'bg-emerald-500',
  caution: 'bg-amber-500',
  serious: 'bg-orange-500',
  critical: 'bg-red-500',
  info: 'bg-sky-500',
  accent: 'bg-chocolate',
  special: 'bg-violet-500',
}

/** Bar fill for a 0–100 meter. Same three thresholds everywhere in the feature. */
export function meterTone(pct: number): string {
  if (pct >= 70) return 'bg-emerald-500'
  if (pct >= 40) return 'bg-amber-500'
  return 'bg-red-500'
}

function Chip({ tone, label, dot, title }: {
  tone: Tone; label: string; dot?: boolean; title?: string
}) {
  return (
    // `data-tone` is a test hook: a dark-mode check needs to find a chip by what
    // it MEANS, not by matching its text against a regex that also matches the
    // paragraph mentioning it.
    <span className={cn(CHIP, TONE[tone])} title={title} data-tone={tone}>
      {dot && <span className={cn('erp-status-dot', TONE_DOT[tone])} />}
      {label}
    </span>
  )
}

// ── roles ───────────────────────────────────────────────────────────────────
//
// `level` and `shallowestUnit` mirror `MobilizerRole` on the server. The rule is
// ONE bound: a role may be assigned at its own level or anywhere BELOW it, never
// above. A county coordinator covering their county and helping in one of its
// wards is two legal assignment rows; a ward captain assigned to a county is a
// promotion by data entry, and the server refuses it.

export interface RoleMeta {
  label: string
  tone: Tone
  level: number
  shallowestUnit: string
}

export const ROLES: Record<MobilizerRoleCode, RoleMeta> = {
  NATIONAL_DIRECTOR: { label: 'National Director', tone: 'special', level: 0, shallowestUnit: 'COUNTRY' },
  NATIONAL_COORDINATOR: { label: 'National Coordinator', tone: 'special', level: 1, shallowestUnit: 'COUNTRY' },
  COUNTY_COORDINATOR: { label: 'County Coordinator', tone: 'accent', level: 2, shallowestUnit: 'COUNTY' },
  CONSTITUENCY_CAPTAIN: { label: 'Constituency Captain', tone: 'info', level: 3, shallowestUnit: 'CONSTITUENCY' },
  WARD_CAPTAIN: { label: 'Ward Captain', tone: 'positive', level: 4, shallowestUnit: 'WARD' },
  MOBILIZER: { label: 'Mobilizer', tone: 'neutral', level: 5, shallowestUnit: 'WARD' },
  CHIEF_AGENT: { label: 'Chief Agent', tone: 'serious', level: 5, shallowestUnit: 'WARD' },
  POLLING_AGENT: { label: 'Polling Agent', tone: 'caution', level: 6, shallowestUnit: 'POLLING_STATION' },
  PARTY_AGENT: { label: 'Party Agent', tone: 'caution', level: 6, shallowestUnit: 'POLLING_STATION' },
  WATCHER: { label: 'Watcher', tone: 'neutral', level: 7, shallowestUnit: 'POLLING_STATION' },
  UNKNOWN: { label: 'Unknown role', tone: 'neutral', level: 99, shallowestUnit: 'UNKNOWN' },
}

/** Ordered by level, so every dropdown and every grouped list agrees. */
export const ROLE_ORDER = (Object.keys(ROLES) as MobilizerRoleCode[])
  .filter((c) => c !== 'UNKNOWN')
  .sort((a, b) => ROLES[a].level - ROLES[b].level)

export const roleLabel = (code?: string | null) =>
  ROLES[(code ?? 'UNKNOWN') as MobilizerRoleCode]?.label ?? code ?? '—'

export function RoleBadge({ code }: { code?: string | null }) {
  const meta = ROLES[(code ?? 'UNKNOWN') as MobilizerRoleCode]
  if (!meta) return <Chip tone="neutral" label={code ?? '—'} />
  return <Chip tone={meta.tone} label={meta.label} />
}

// ── statuses ────────────────────────────────────────────────────────────────

const MOBILIZER_STATUS: Record<MobilizerStatusCode, { label: string; tone: Tone }> = {
  ACTIVE: { label: 'Active', tone: 'positive' },
  INACTIVE: { label: 'Inactive', tone: 'neutral' },
  SUSPENDED: { label: 'Suspended', tone: 'caution' },
  TERMINATED: { label: 'Terminated', tone: 'critical' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}

export const STATUS_ORDER: MobilizerStatusCode[] = ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'TERMINATED']

export const statusLabel = (code?: string | null) =>
  MOBILIZER_STATUS[(code ?? 'UNKNOWN') as MobilizerStatusCode]?.label ?? code ?? '—'

export function StatusChip({ code }: { code?: string | null }) {
  const meta = MOBILIZER_STATUS[(code ?? 'UNKNOWN') as MobilizerStatusCode]
  if (!meta) return <Chip tone="neutral" label={code ?? '—'} dot />
  return <Chip tone={meta.tone} label={meta.label} dot />
}

// ── adoption ────────────────────────────────────────────────────────────────

const ADOPTION_STATUS: Record<AdoptionStatusCode, { label: string; tone: Tone }> = {
  ACTIVE: { label: 'Adopted', tone: 'positive' },
  RELEASED: { label: 'Released', tone: 'neutral' },
  // Not "replaced": a reassignment is a decision somebody made, and the row it
  // points at names who took over.
  SUPERSEDED: { label: 'Superseded', tone: 'info' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}

export function AdoptionChip({ code }: { code?: string | null }) {
  const meta = ADOPTION_STATUS[(code ?? 'UNKNOWN') as AdoptionStatusCode]
  return <Chip tone={meta?.tone ?? 'neutral'} label={meta?.label ?? code ?? '—'} dot />
}

// ── coverage ────────────────────────────────────────────────────────────────
//
// GAP wins over everything: a unit with no mobilizers is not "understaffed", it
// is uncovered, and the two call for different actions.

const COVERAGE: Record<CoverageStatusCode, { label: string; tone: Tone; hint: string }> = {
  GAP: { label: 'No mobilizer', tone: 'critical', hint: 'Nobody is assigned here' },
  UNDERSTAFFED: { label: 'Understaffed', tone: 'caution', hint: 'More voters per mobilizer than the configured ratio' },
  OK: { label: 'Healthy', tone: 'positive', hint: 'Within the configured ratios' },
  OVERSTAFFED: { label: 'Overstaffed', tone: 'info', hint: 'Fewer voters per mobilizer than the configured ratio — consider redeploying' },
}

export const COVERAGE_ORDER: CoverageStatusCode[] = ['GAP', 'UNDERSTAFFED', 'OK', 'OVERSTAFFED']

export const coverageLabel = (code?: string | null) =>
  COVERAGE[(code ?? '') as CoverageStatusCode]?.label ?? code ?? '—'

export function CoverageChip({ code }: { code?: string | null }) {
  const meta = COVERAGE[(code ?? '') as CoverageStatusCode]
  if (!meta) return <Chip tone="neutral" label={code ?? '—'} dot />
  return <Chip tone={meta.tone} label={meta.label} dot title={meta.hint} />
}

// ── activity ────────────────────────────────────────────────────────────────
//
// EVERY type the server can send. Nine were missing — the turnout ones, the
// programme delivery and the quality flag — and a code with no entry here falls
// through to the raw enum, so the field feed showed `PLEDGE_CAPTURED` in a row of
// sentences and the filter dropdown could not offer it at all.
//
// `DISBURSEMENT_LOGGED` is deliberately absent: it is not an activity type the
// server has, and one nothing writes is a lie in a filter dropdown.

export const ACTIVITY: Record<ActivityTypeCode, { label: string; tone: Tone }> = {
  CHECK_IN: { label: 'Check-in', tone: 'positive' },
  CHECK_OUT: { label: 'Check-out', tone: 'neutral' },
  VOTER_REGISTERED: { label: 'Voter registered', tone: 'positive' },
  VOTER_VERIFIED: { label: 'Voter verified', tone: 'info' },
  VOTER_CONTACTED: { label: 'Voter contacted', tone: 'info' },
  SURVEY_COMPLETED: { label: 'Survey completed', tone: 'special' },
  MOOD_REPORTED: { label: 'Mood reported', tone: 'caution' },
  INCIDENT_REPORTED: { label: 'Incident reported', tone: 'critical' },
  PROGRAM_ENROLLED: { label: 'Programme enrolment', tone: 'special' },
  TRANSFER_RAISED: { label: 'Transfer raised', tone: 'accent' },
  TRANSFER_RECORDED: { label: 'Transfer recorded', tone: 'accent' },
  CALL_MADE: { label: 'Call made', tone: 'info' },
  DOOR_TO_DOOR: { label: 'Door to door', tone: 'accent' },
  MEETING_ATTENDED: { label: 'Meeting attended', tone: 'special' },
  NOTE_ADDED: { label: 'Note added', tone: 'neutral' },
  PROGRAM_DELIVERED: { label: 'Programme delivery', tone: 'special' },
  FIELD_QUALITY_FLAGGED: { label: 'Quality flagged', tone: 'critical' },
  CELL_FORMED: { label: 'Cell formed', tone: 'accent' },
  CELL_MEMBER_ENROLLED: { label: 'Cell member enrolled', tone: 'accent' },
  PLEDGE_CAPTURED: { label: 'Pledge taken', tone: 'positive' },
  TURNOUT_CHECKIN: { label: 'Turnout check-in', tone: 'positive' },
  QUEUE_REPORTED: { label: 'Queue reported', tone: 'caution' },
  INCENTIVE_DELIVERED: { label: 'Incentive delivered', tone: 'info' },
  ESCALATION_RESOLVED: { label: 'Escalation resolved', tone: 'positive' },
  UNKNOWN: { label: 'Unknown activity', tone: 'neutral' },
}

export const ACTIVITY_ORDER = (Object.keys(ACTIVITY) as ActivityTypeCode[])
  .filter((c) => c !== 'UNKNOWN')

export const activityLabel = (code?: string | null) =>
  ACTIVITY[(code ?? 'UNKNOWN') as ActivityTypeCode]?.label ?? code ?? '—'

export function ActivityChip({ code }: { code?: string | null }) {
  const meta = ACTIVITY[(code ?? 'UNKNOWN') as ActivityTypeCode]
  return <Chip tone={meta?.tone ?? 'neutral'} label={meta?.label ?? code ?? '—'} />
}

/**
 * What the campaign can actually capture today. Two of the fifteen types are
 * written by `relocation`'s attribution listener and must not be offered as a
 * manual entry — a hand-typed TRANSFER_RAISED is a second row for one event, and
 * the leaderboard would count it twice.
 */
export const CAPTURABLE: ActivityTypeCode[] = [
  'CHECK_IN', 'CHECK_OUT', 'VOTER_CONTACTED', 'CALL_MADE', 'DOOR_TO_DOOR',
  'MEETING_ATTENDED', 'MOOD_REPORTED', 'INCIDENT_REPORTED', 'NOTE_ADDED',
]

const SOURCE: Record<ActivitySourceCode, { label: string; tone: Tone; hint: string }> = {
  FIELD: { label: 'Field', tone: 'neutral', hint: 'Captured by the mobilizer' },
  SYSTEM: { label: 'System', tone: 'info', hint: 'Credited automatically when work was recorded elsewhere' },
  IMPORT: { label: 'Import', tone: 'caution', hint: 'Backfilled on somebody else’s behalf' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral', hint: '' },
}

export function SourceChip({ code }: { code?: string | null }) {
  const meta = SOURCE[(code ?? 'UNKNOWN') as ActivitySourceCode]
  if (!meta || code === 'FIELD') return null
  return <Chip tone={meta.tone} label={meta.label} title={meta.hint} />
}

// ── missions and tasks ──────────────────────────────────────────────────────

export const MISSION_KIND: Record<MissionKindCode, { label: string; available: boolean; note?: string }> = {
  ADOPT_STATION: { label: 'Adopt a station', available: true },
  VERIFY_VOTERS: { label: 'Verify voters', available: true },
  CONTACT_VOTERS: { label: 'Contact voters', available: true },
  CHECK_IN: { label: 'Daily check-in', available: true },
  // Defined so the vocabulary is stable, refused at creation so nobody assigns
  // work against a feature that does not exist yet.
  SURVEY_COLLECTION: { label: 'Survey collection', available: false, note: 'Arrives with Polling Studio' },
  PROGRAM_ENROLMENT: { label: 'Programme enrolment', available: false, note: 'Arrives with Programmes' },
  UNKNOWN: { label: 'Unknown kind', available: false },
}

export const MISSION_KIND_ORDER = (Object.keys(MISSION_KIND) as MissionKindCode[])
  .filter((c) => c !== 'UNKNOWN')

export const missionKindLabel = (code?: string | null) =>
  MISSION_KIND[(code ?? 'UNKNOWN') as MissionKindCode]?.label ?? code ?? '—'

const MISSION_STATUS: Record<MissionStatusCode, { label: string; tone: Tone }> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  ACTIVE: { label: 'Active', tone: 'positive' },
  PAUSED: { label: 'Paused', tone: 'caution' },
  COMPLETED: { label: 'Completed', tone: 'info' },
  CANCELLED: { label: 'Cancelled', tone: 'critical' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}

export const MISSION_STATUS_ORDER: MissionStatusCode[] =
  ['DRAFT', 'ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELLED']

export function MissionStatusChip({ code }: { code?: string | null }) {
  const meta = MISSION_STATUS[(code ?? 'UNKNOWN') as MissionStatusCode]
  return <Chip tone={meta?.tone ?? 'neutral'} label={meta?.label ?? code ?? '—'} dot />
}

const TASK_STATUS: Record<TaskStatusCode, { label: string; tone: Tone }> = {
  ASSIGNED: { label: 'Assigned', tone: 'neutral' },
  ACCEPTED: { label: 'Accepted', tone: 'info' },
  IN_PROGRESS: { label: 'In progress', tone: 'accent' },
  COMPLETED: { label: 'Completed', tone: 'positive' },
  EXPIRED: { label: 'Expired', tone: 'critical' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}

export const TASK_STATUS_ORDER: TaskStatusCode[] =
  ['ASSIGNED', 'ACCEPTED', 'IN_PROGRESS', 'COMPLETED', 'EXPIRED', 'CANCELLED']

export const taskStatusLabel = (code?: string | null) =>
  TASK_STATUS[(code ?? 'UNKNOWN') as TaskStatusCode]?.label ?? code ?? '—'

export function TaskStatusChip({ code }: { code?: string | null }) {
  const meta = TASK_STATUS[(code ?? 'UNKNOWN') as TaskStatusCode]
  return <Chip tone={meta?.tone ?? 'neutral'} label={meta?.label ?? code ?? '—'} dot />
}

const PRIORITY: Record<TaskPriorityCode, { label: string; tone: Tone }> = {
  HIGH: { label: 'High', tone: 'critical' },
  MEDIUM: { label: 'Medium', tone: 'caution' },
  LOW: { label: 'Low', tone: 'info' },
  UNKNOWN: { label: 'Unknown', tone: 'neutral' },
}

export const PRIORITY_ORDER: TaskPriorityCode[] = ['HIGH', 'MEDIUM', 'LOW']

export function PriorityChip({ code }: { code?: string | null }) {
  const meta = PRIORITY[(code ?? 'UNKNOWN') as TaskPriorityCode]
  return <Chip tone={meta?.tone ?? 'neutral'} label={meta?.label ?? code ?? '—'} />
}

// ── tiers and badges ────────────────────────────────────────────────────────
//
// Tier thresholds live in `mobilizer.tier.thresholds` on the server and can be
// edited. Nothing here hardcodes a number — the tier arrives already decided,
// and the ORDER below is only for laying the distribution out consistently.

export const TIER_ORDER: TierCode[] = ['BRONZE', 'SILVER', 'GOLD', 'PLATINUM', 'DIAMOND']

const TIER: Record<TierCode, { label: string; tone: Tone }> = {
  BRONZE: { label: 'Bronze', tone: 'serious' },
  SILVER: { label: 'Silver', tone: 'neutral' },
  GOLD: { label: 'Gold', tone: 'caution' },
  PLATINUM: { label: 'Platinum', tone: 'info' },
  DIAMOND: { label: 'Diamond', tone: 'special' },
  UNKNOWN: { label: 'Unrated', tone: 'neutral' },
}

export const tierLabel = (code?: string | null) =>
  TIER[(code ?? 'UNKNOWN') as TierCode]?.label ?? code ?? '—'

export function TierChip({ code }: { code?: string | null }) {
  if (!code) return <span className="text-muted-foreground">—</span>
  const meta = TIER[code as TierCode]
  return <Chip tone={meta?.tone ?? 'neutral'} label={meta?.label ?? code} />
}

/**
 * The ten badges, with what earns each.
 *
 * The criterion is shown in the chip's tooltip and printed in full on the
 * leaderboard's legend. A badge nobody can explain is a badge people assume is
 * random; one that shows its working survives the first challenge.
 */
export const BADGES: Record<BadgeCode, { label: string; earns: string }> = {
  TOP_RATED: { label: 'Top rated', earns: 'Highest score in the county this week' },
  CLOSER: { label: 'Closer', earns: 'Most tasks completed in the county this week' },
  HIGH_PERFORMER: { label: 'High performer', earns: 'Top tenth of the county by score' },
  ACTIVITY_BEAST: { label: 'Activity beast', earns: 'Most unflagged activities in the county this week' },
  DATA_HUNGRY: { label: 'Data hungry', earns: 'Most voters registered in the county this week' },
  STREAK_MASTER: { label: 'Streak master', earns: 'Fourteen consecutive active days' },
  VOTER_WHIRLWIND: { label: 'Voter whirlwind', earns: 'Two hundred voters contacted in the week' },
  PEOPLE_LEADER: { label: 'People leader', earns: 'Three or more subordinates, all scoring above zero' },
  SURVEY_PRO: { label: 'Survey pro', earns: 'Most surveys completed — dormant until Polling Studio ships' },
  MOMENTUM: { label: 'Momentum', earns: 'Score at least half again on last week, from a base of 100' },
  UNKNOWN: { label: 'Unknown badge', earns: '' },
}

export function BadgeChip({ code }: { code?: string | null }) {
  const meta = BADGES[(code ?? 'UNKNOWN') as BadgeCode]
  return (
    <span
      className={cn(CHIP, TONE.accent)}
      title={meta?.earns || undefined}
    >
      {meta?.label ?? code ?? '—'}
    </span>
  )
}

// ── small shared formatters ─────────────────────────────────────────────────

/** `1:340`, or an em-dash when the ratio is undefined. NEVER `1:0` or `1:∞`. */
export function ratio(votersPerMobilizer?: number | null): string {
  if (votersPerMobilizer === null || votersPerMobilizer === undefined) return '—'
  if (!Number.isFinite(votersPerMobilizer)) return '—'
  return `1:${Math.round(votersPerMobilizer).toLocaleString('en-KE')}`
}

/** `65%` of a part/whole, or an em-dash when the whole is zero. */
export function pct(part: number, whole: number): string {
  if (!whole) return '—'
  return `${Math.round((part / whole) * 100)}%`
}

export function pctValue(part: number, whole: number): number {
  if (!whole) return 0
  return Math.round((part / whole) * 100)
}

/** The score arrives as a JSON number or a string (BigDecimal). Both are fine. */
export function scoreNumber(v?: string | number | null): number | null {
  if (v === null || v === undefined) return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}
